import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Sentry } from '@/lib/sentry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary — catches React render crashes and shows
 * a visible recovery screen instead of a blank/black page.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);

    // Report to Sentry with component stack as extra context
    Sentry.withScope((scope) => {
      scope.setExtra('componentStack', errorInfo.componentStack);
      Sentry.captureException(error);
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleClearAndReload = () => {
    // Clear tour state and any other cached state that might cause loops
    try {
      localStorage.removeItem('sv-tour-active');
      localStorage.removeItem('sv-tour-step');
      localStorage.removeItem('sv-tour-progress');
      localStorage.removeItem('sv-login-streak');
    } catch {
      // Ignore localStorage errors
    }
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#0a0a0a',
            color: '#f5f5f5',
            fontFamily: 'Inter, system-ui, sans-serif',
            padding: '24px',
          }}
        >
          <div style={{ maxWidth: '400px', textAlign: 'center' }}>
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: '#dc2626',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '24px',
                fontWeight: 700,
                color: '#fff',
              }}
            >
              SV
            </div>
            <h1 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '8px' }}>
              Something went wrong
            </h1>
            <p style={{ fontSize: '14px', color: '#a1a1a1', marginBottom: '24px', lineHeight: 1.5 }}>
              Scholar Vision ran into an unexpected error. Try reloading the page.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  backgroundColor: '#dc2626',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '14px',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Reload Page
              </button>
              <button
                onClick={this.handleClearAndReload}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  backgroundColor: '#333',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '14px',
                  border: '1px solid #555',
                  cursor: 'pointer',
                }}
              >
                Clear Cache & Reload
              </button>
            </div>
            {this.state.error && (
              <p
                style={{
                  marginTop: '24px',
                  fontSize: '11px',
                  color: '#666',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                }}
              >
                {this.state.error.message}
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
